import { useEffect } from "react";
import { useSocialStore } from "../../../stores/social";
import useModals from "../../../hooks/useModals";
import {
  getEmployee,
  unvalidateEmployee,
  validateEmployee,
} from "../../../api/social";
import Informations from "./Informations";
import ElementsDetails from "./ElementsDetails";
import TabArrayElements from "./TabArrayElements";
import TableElements from "./TableElements";
import { useUserContext } from "../../../contexts/userContext";
import { showToastError, showToastSuccess } from "../../../services/toast";
import { useTranslation } from "react-i18next";

const Elements = () => {
  const { t } = useTranslation();
  const {
    dataSocialFilter,
    tabElements,
    idEmployee,
    formData,
    setFormData,
    updateSocial,
    customer,
    setUpdateSocial,
  } = useSocialStore();
  const { typeRole } = useUserContext();

  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  useEffect(() => {
    getEmployee(idEmployee)
      .then((res) => {
        const formattedData = {
          ...res.data,
          idCommentary: res.data.sc_MonthlyMiscDatas[0]?.id,
          commentary: res.data.sc_MonthlyMiscDatas[0]?.data,
        };
        setFormData(formattedData);
      })
      .catch((err) => {
        showToastError(err.response.data.message);
      });
  }, [dataSocialFilter, updateSocial]);

  const handleFinishInput = () => {
    if (formData.isFinished === false) {
      validateEmployee(idEmployee)
        .then(() => {
          showToastSuccess(t("Employé validé avec succès"));
          setUpdateSocial(!updateSocial);
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    } else {
      unvalidateEmployee(idEmployee)
        .then(() => {
          showToastSuccess(t("Employé dévalidé avec succès"));
          setUpdateSocial(!updateSocial);
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    }
  };

  return (
    <div className="flex h-full w-full gap-3 bg-slate-50 p-3">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}

      {/* Colonne de gauche - Informations - Optimisé pour PC portable */}
      <div className="shrink-0">
        <Informations />
      </div>

      {/* Colonne de droite - Tableau des éléments - Optimisé pour PC portable */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 h-full overflow-hidden flex flex-col">
          {/* En-tête avec bouton d'ajout - Compact */}
          <div className="bg-thead px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-1.5">
                  <svg
                    className="w-4 h-4 text-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <h3 className="text-black font-semibold text-base font-Nunito truncate">
                  {tabElements === "Absences"
                    ? t("Gestion des absences")
                    : t("Gestion des valeurs")}
                </h3>
              </div>

              <div className="flex items-center gap-2">
                {customer?.sc_Completed === false && (
                  <button
                    className="group bg-white text-blue-600 px-3 py-2 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-1.5 font-medium text-sm shrink-0 cursor-pointer"
                    onClick={() => {
                      handleFinishInput();
                    }}
                  >
                    {formData.isFinished === false
                      ? t("Fin de saisie")
                      : t("Annuler la fin de saisie")}
                  </button>
                )}

                {(typeRole === "Company" ||
                  (typeRole === "Customer" &&
                    customer?.sc_Completed === false)) && (
                  <button
                    onClick={() => {
                      openModal(
                        "elementsDetails",
                        <ElementsDetails
                          closeModal={() => closeModal("elementsDetails")}
                          createOrEdit="create"
                        />,
                      );
                    }}
                    disabled={customer?.sc_PaySlipsValidated === true}
                    className={`group bg-white text-blue-600 px-3 py-2 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-1.5 font-medium text-sm shrink-0 ${customer?.sc_PaySlipsValidated === true ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    {t("Ajouter")}{" "}
                    {tabElements === "Absences" ? t("une absence") : t("une valeur")}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Onglets */}
            <div className="bg-gray-50 border-b border-gray-200">
              <TabArrayElements />
            </div>

            {/* Tableau */}
            <div className="flex min-h-0 flex-1 flex-col">
              {tabElements === "Absences" ? (
                <TableElements
                  columns={[
                    { header: t("Type"), accessor: "nameTypeInput" },
                    { header: t("Début"), accessor: "from" },
                    { header: t("Fin"), accessor: "to" },
                    { header: t("Matin"), accessor: "morning" },
                    { header: t("Après midi"), accessor: "afternoon" },
                    { header: t("Prolongation"), accessor: "previousFrom" },
                    { header: t("Commentaire"), accessor: "commentary" },
                  ]}
                  data={formData?.sc_Absences}
                />
              ) : (
                <TableElements
                  columns={[
                    { header: t("Type"), accessor: "nameTypeInput" },
                    { header: t("Valeur"), accessor: "amount" },
                    { header: t("Commentaire"), accessor: "commentary" },
                  ]}
                  data={formData?.sc_Monthly}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Elements;
